import { useState, useEffect, useMemo, useCallback } from "react";
import { ChevronsUpDown, X } from "lucide-react";

import { NormalizedMemberData, isWorkspaceInvite } from "../hooks/utils";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import Checkbox from "@/components/ui/checkbox";
import { useGetOrganizationRoles } from "@/queries/organizations";
import { useGetWorkspaceMemberRoles, useUpdateWorkspaceMemberRoles } from "@/queries/workspace";
import { Skeleton } from "@/components/ui/skeleton";
import { capitalize } from "@/utils/strings";
import { PERMISSION_LABELS, Permissions } from "@/providers/RbacProvider/constants";

const groupPermissions = (permissions: string[]) =>
  permissions.reduce(
    (acc, permission) => {
      const { action, entity } = PERMISSION_LABELS[permission as Permissions];
      const capitalizedAction = capitalize(action);
      const capitalizedEntity = capitalize(entity);

      return {
        ...acc,
        [capitalizedAction]: [...(acc[capitalizedAction] || []), capitalizedEntity],
      };
    },
    {} as Record<string, string[]>,
  );

interface ManageRolesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: NormalizedMemberData | null;
  workspaceId: number;
  organizationId: number;
}

export const ManageRolesDialog = ({
  open,
  onOpenChange,
  member,
  workspaceId,
  organizationId,
}: ManageRolesDialogProps) => {
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);

  const { data: organizationRolesData, isInitialLoading: isLoadingOrgRoles } =
    useGetOrganizationRoles(organizationId, open);

  const getMemberId = () => {
    if (!member) {
      return 0;
    }
    return isWorkspaceInvite(member) ? member.invitee_id : member.user_id;
  };
  const memberId = getMemberId();

  const { data: memberRolesData, isInitialLoading: isLoadingMemberRoles } =
    useGetWorkspaceMemberRoles(workspaceId, memberId || 0, open && Boolean(memberId));

  const { mutate: updateRoles, isPending: isUpdating } = useUpdateWorkspaceMemberRoles();

  const organizationRoles = useMemo(
    () => organizationRolesData?.roles || [],
    [organizationRolesData?.roles],
  );

  useEffect(() => {
    if (member?.id) {
      const roleIds = memberRolesData?.roles?.map(role => role.id) || [];
      setSelectedRoleIds(roleIds);
    }
  }, [open, member?.id, memberRolesData]);

  const selectedRoles = useMemo(
    () =>
      selectedRoleIds
        .map(id => organizationRoles.find(role => role.id === id))
        .filter((role): role is NonNullable<typeof role> => Boolean(role)),
    [selectedRoleIds, organizationRoles],
  );

  const handleToggleRole = useCallback((roleId: number) => {
    setSelectedRoleIds(prev =>
      prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId],
    );
  }, []);

  const handleSave = () => {
    if (!member || !memberId) {
      return;
    }

    updateRoles(
      {
        workspaceId,
        memberId: memberId,
        roleIds: selectedRoleIds,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  };

  const isLoading = isLoadingOrgRoles || isLoadingMemberRoles;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manage Roles {member?.name ? `for ${member.name}` : ""}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Select roles to assign to this workspace member. Deselecting all roles will revoke all
            permissions.
          </p>

          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <Popover modal>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="h-auto min-h-[40px] w-full justify-between"
                  disabled={isUpdating}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                      <div
                        key={-1}
                        className="inline-flex max-w-full items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-800"
                      >
                        <span className="truncate">Member</span>
                      </div>
                      {selectedRoles.map(role => (
                        <div
                          key={role.id}
                          className="inline-flex max-w-full items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-sm text-blue-800"
                        >
                          <span className="truncate">{role.name}</span>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setSelectedRoleIds(prev => prev.filter(id => id !== role.id));
                            }}
                            className="flex-shrink-0 rounded-sm p-0.5 hover:bg-blue-200"
                            disabled={isUpdating}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <ChevronsUpDown className="w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                style={{ width: "var(--radix-popover-trigger-width)" }}
                className="max-h-[300px] overflow-hidden p-0 px-1"
              >
                <Command className="max-h-[300px] pb-2">
                  <CommandInput
                    placeholder="Search roles..."
                    disabled={isUpdating}
                    className="mb-1 border-b !border-gray-200"
                  />
                  <CommandList>
                    <CommandEmpty>No roles found.</CommandEmpty>
                    <CommandGroup>
                      <div className="flex flex-col gap-2">
                        <CommandItem
                          key={-1}
                          value="Member"
                          disabled
                          className="cursor-pointer flex-col items-start gap-0"
                        >
                          <div className="flex w-full items-center">
                            <Checkbox checked className="mr-1.5" disabled={isUpdating} />
                            <span className="font-medium">Member</span>
                          </div>
                          <div key="Read">
                            <span className="ml-6 mt-1 text-xs text-muted-foreground">
                              Read: Prompts, Workflows, Datasets, Evaluations
                            </span>
                          </div>
                        </CommandItem>
                        {organizationRoles.map(role => {
                          const permissions = groupPermissions(role.permissions);
                          return (
                            <CommandItem
                              key={role.id}
                              value={role.name}
                              onSelect={() => {
                                if (!isUpdating) {
                                  handleToggleRole(role.id);
                                }
                              }}
                              disabled={isUpdating}
                              className="cursor-pointer flex-col items-start gap-0"
                            >
                              <div className="flex w-full items-center">
                                <Checkbox
                                  checked={selectedRoleIds.includes(role.id)}
                                  className="mr-1.5"
                                  disabled={isUpdating}
                                />
                                <span className="font-medium">{role.name}</span>
                              </div>
                              {role.permissions.length > 0 &&
                                Object.entries(permissions).map(([action, entities]) => {
                                  if (entities.length === 0) {
                                    return null;
                                  }
                                  return (
                                    <div key={action}>
                                      <span className="ml-6 mt-1 text-xs text-muted-foreground">
                                        {action}: {entities.join(", ")}
                                      </span>
                                    </div>
                                  );
                                })}
                            </CommandItem>
                          );
                        })}
                      </div>
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isUpdating || isLoading}>
            {isUpdating ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
